<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-header-left">
        <router-link to="/client/inventory/transfer-orders" class="back-btn"><i class="ri-arrow-left-line"></i></router-link>
        <h1 class="page-title">調撥單詳情</h1>
      </div>
      <div class="header-actions">
        <span v-if="order" :class="['badge', statusClass(order.status)]" style="font-size:14px; margin-right:12px">
          {{ statusLabel(order.status) }}
        </span>
        <button v-if="canCancel" class="btn btn-outline-danger btn-sm" @click="cancelOrder">取消調撥單</button>
      </div>
    </div>

    <div v-if="!order && loading" class="loading-state">載入中...</div>

    <template v-if="order">
      <div class="info-card">
        <div class="info-grid">
          <div class="info-item"><span class="info-label">單號</span><code>{{ order.orderNo }}</code></div>
          <div class="info-item"><span class="info-label">出貨倉庫</span>{{ order.fromWarehouseName }}</div>
          <div class="info-item"><span class="info-label">目標倉庫</span>{{ order.toWarehouseName }}</div>
          <div class="info-item"><span class="info-label">出貨時間</span>{{ order.shippedAt ? formatDate(order.shippedAt) : '-' }}</div>
          <div class="info-item"><span class="info-label">完成時間</span>{{ order.completedAt ? formatDate(order.completedAt) : '-' }}</div>
          <div class="info-item"><span class="info-label">備註</span>{{ order.notes || '-' }}</div>
          <div class="info-item">
            <span class="info-label">建立者</span>
            <span v-if="order.createdByName">
              {{ order.createdByName }}
              <span class="text-muted" style="font-size:12px">（{{ order.createdByUsername }}）</span>
            </span>
            <span v-else>-</span>
          </div>
          <div class="info-item"><span class="info-label">建立時間</span>{{ formatDate(order.createdAt) }}</div>
        </div>
      </div>

      <div class="form-card">
        <div class="section-header">
          <h3 class="section-title">調撥明細</h3>
          <div class="action-buttons">
            <button v-if="canConfirm" class="btn btn-primary btn-sm" :disabled="processing" @click="confirmOrder">
              確認調撥
            </button>
            <button v-if="canShip" class="btn btn-primary btn-sm" :disabled="processing" @click="openShipModal">
              執行出貨
            </button>
            <button v-if="canReceive" class="btn btn-success btn-sm" :disabled="processing" @click="openReceiveModal">
              確認入庫
            </button>
          </div>
        </div>

        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>商品名稱</th>
                <th>規格 SKU</th>
                <th>申請數量</th>
                <th>已出貨</th>
                <th>已入庫</th>
                <th>入庫進度</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in order.items" :key="item.id">
                <td>{{ item.productName }}</td>
                <td><code>{{ item.variantSku }}</code></td>
                <td>{{ item.requestedQuantity }}</td>
                <td>{{ item.shippedQuantity }}</td>
                <td>{{ item.receivedQuantity }}</td>
                <td>
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: receivePct(item) + '%' }"></div>
                  </div>
                  <span class="progress-label">{{ receivePct(item) }}%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- 出貨 Modal -->
    <div v-if="showShipModal" class="modal-overlay" @click.self="showShipModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>執行出貨</h3>
          <button class="modal-close" @click="showShipModal = false"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <p class="modal-tip">請填寫各品項實際出貨數量</p>
          <div v-for="item in shipItems" :key="item.itemId" class="action-item">
            <div class="action-item-name">{{ item.productName }} <code>{{ item.variantSku }}</code></div>
            <div class="action-item-meta">申請調撥：{{ item.requestedQuantity }} 件</div>
            <div class="action-item-input">
              <label>實際出貨：</label>
              <input v-model.number="item.qty" type="number" :min="0" :max="item.requestedQuantity" class="form-control input-sm" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showShipModal = false">取消</button>
          <button class="btn btn-primary" :disabled="processing" @click="submitShip">
            {{ processing ? '處理中...' : '確認出貨' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 入庫 Modal -->
    <div v-if="showReceiveModal" class="modal-overlay" @click.self="showReceiveModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>確認入庫</h3>
          <button class="modal-close" @click="showReceiveModal = false"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <p class="modal-tip">請填寫各品項實際收到數量</p>
          <div v-for="item in receiveItems" :key="item.itemId" class="action-item">
            <div class="action-item-name">{{ item.productName }} <code>{{ item.variantSku }}</code></div>
            <div class="action-item-meta">已出貨：{{ item.shippedQuantity }} 件 ／ 已入庫：{{ item.receivedQuantity }} 件</div>
            <div class="action-item-input">
              <label>本次入庫：</label>
              <input v-model.number="item.qty" type="number" :min="0" class="form-control input-sm" />
            </div>
            <div class="action-item-input">
              <label>備註：</label>
              <input v-model="item.note" class="form-control" placeholder="如有數量差異請說明" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showReceiveModal = false">取消</button>
          <button class="btn btn-primary" :disabled="processing" @click="submitReceive">
            {{ processing ? '處理中...' : '確認入庫' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { transferOrderService } from '@/api/modules/warehouses.js';

const route = useRoute();
const order = ref(null);
const loading = ref(false);
const processing = ref(false);
const showShipModal = ref(false);
const showReceiveModal = ref(false);
const shipItems = ref([]);
const receiveItems = ref([]);

const STATUS_LABELS = { draft: '草稿', confirmed: '已確認', shipped: '已出貨', partially_received: '部分入庫', completed: '已完成', cancelled: '已取消' };
const STATUS_CLASSES = { draft: 'badge-secondary', confirmed: 'badge-primary', shipped: 'badge-info', partially_received: 'badge-warning', completed: 'badge-success', cancelled: 'badge-danger' };

const statusLabel = (s) => STATUS_LABELS[s] || s;
const statusClass = (s) => STATUS_CLASSES[s] || 'badge-secondary';
const formatDate = (d) => d ? new Date(d).toLocaleString('zh-TW') : '-';
const receivePct = (item) => item.shippedQuantity > 0
  ? Math.min(100, Math.round(item.receivedQuantity / item.shippedQuantity * 100))
  : 0;

const canConfirm = computed(() => order.value?.status === 'draft');
const canShip = computed(() => order.value?.status === 'confirmed');
const canReceive = computed(() => ['shipped', 'partially_received'].includes(order.value?.status));
const canCancel = computed(() => ['draft', 'confirmed'].includes(order.value?.status));

async function fetchOrder() {
  loading.value = true;
  try {
    const res = await transferOrderService.getTransferOrderById(route.params.id);
    order.value = res.data || null;
  } catch (e) { console.error(e); } finally { loading.value = false; }
}

async function confirmOrder() {
  if (!confirm('確定要確認此調撥單嗎？確認後將鎖定總倉庫存。')) return;
  processing.value = true;
  try {
    await transferOrderService.confirmTransferOrder(route.params.id);
    fetchOrder();
  } catch (e) { alert(e.response?.data?.message || '操作失敗'); } finally { processing.value = false; }
}

function openShipModal() {
  shipItems.value = order.value.items.map(i => ({
    itemId: i.id, productName: i.productName, variantSku: i.variantSku,
    requestedQuantity: i.requestedQuantity, qty: i.requestedQuantity
  }));
  showShipModal.value = true;
}

async function submitShip() {
  const items = shipItems.value.map(i => ({ itemId: i.itemId, shippedQuantity: i.qty }));
  processing.value = true;
  try {
    await transferOrderService.shipTransferOrder(route.params.id, items);
    showShipModal.value = false;
    fetchOrder();
  } catch (e) { alert(e.response?.data?.message || '操作失敗'); } finally { processing.value = false; }
}

function openReceiveModal() {
  receiveItems.value = order.value.items
    .filter(i => i.receivedQuantity < i.shippedQuantity)
    .map(i => ({
      itemId: i.id, productName: i.productName, variantSku: i.variantSku,
      shippedQuantity: i.shippedQuantity, receivedQuantity: i.receivedQuantity,
      qty: i.shippedQuantity - i.receivedQuantity, note: ''
    }));
  showReceiveModal.value = true;
}

async function submitReceive() {
  const items = receiveItems.value.filter(i => i.qty > 0)
    .map(i => ({ itemId: i.itemId, receivedQuantity: i.qty, note: i.note || undefined }));
  if (!items.length) { alert('請輸入入庫數量'); return; }
  processing.value = true;
  try {
    await transferOrderService.receiveTransferOrder(route.params.id, items);
    showReceiveModal.value = false;
    fetchOrder();
  } catch (e) { alert(e.response?.data?.message || '操作失敗'); } finally { processing.value = false; }
}

async function cancelOrder() {
  if (!confirm('確定要取消此調撥單嗎？')) return;
  processing.value = true;
  try {
    await transferOrderService.cancelTransferOrder(route.params.id);
    fetchOrder();
  } catch (e) { alert(e.response?.data?.message || '操作失敗'); } finally { processing.value = false; }
}

onMounted(fetchOrder);
</script>

<style scoped>
.page-container { padding: 20px; max-width: 900px; }
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.page-header-left { display: flex; align-items: center; gap: 10px; }
.back-btn { color: #666; font-size: 20px; text-decoration: none; }
.page-title { font-size: 22px; font-weight: 600; margin: 0; }
.header-actions { display: flex; align-items: center; }
.info-card, .form-card { background: #fff; border: 1px solid #e8e8e8; border-radius: 10px; padding: 20px; margin-bottom: 16px; }
.info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.info-item { display: flex; flex-direction: column; gap: 4px; }
.info-label { font-size: 12px; color: #999; }
.section-title { font-size: 15px; font-weight: 600; margin: 0; color: #333; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.action-buttons { display: flex; gap: 8px; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
th { background: #f8f9fa; font-weight: 600; color: #555; }
.progress-bar { width: 80px; height: 6px; background: #eee; border-radius: 3px; display: inline-block; vertical-align: middle; margin-right: 6px; }
.progress-fill { height: 100%; background: #27ae60; border-radius: 3px; }
.progress-label { font-size: 12px; color: #666; }
.loading-state { text-align: center; padding: 40px; color: #999; }
.badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.badge-primary { background: #e3f0ff; color: #1a73e8; }
.badge-secondary { background: #f0f0f0; color: #666; }
.badge-info { background: #e3f8ff; color: #0984e3; }
.badge-success { background: #e6f9ee; color: #27ae60; }
.badge-danger { background: #fde8e8; color: #e74c3c; }
.badge-warning { background: #fff8e1; color: #f39c12; }
.btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-secondary { background: #f0f0f0; color: #333; }
.btn-success { background: #27ae60; color: #fff; }
.btn-outline-danger { background: transparent; border: 1px solid #e74c3c; color: #e74c3c; }
.btn-sm { padding: 4px 12px; font-size: 13px; border-radius: 4px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-box { background: #fff; border-radius: 12px; width: 500px; max-width: 95vw; max-height: 80vh; display: flex; flex-direction: column; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #eee; }
.modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
.modal-close { background: none; border: none; cursor: pointer; font-size: 20px; color: #999; }
.modal-body { padding: 16px 20px; overflow-y: auto; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 20px; border-top: 1px solid #eee; }
.modal-tip { font-size: 13px; color: #666; margin-bottom: 12px; }
.action-item { border: 1px solid #eee; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
.action-item-name { font-weight: 600; font-size: 14px; margin-bottom: 4px; }
.action-item-meta { font-size: 12px; color: #666; margin-bottom: 8px; }
.action-item-input { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 13px; }
.form-control { padding: 6px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; width: 100%; box-sizing: border-box; }
.input-sm { width: 80px; }
</style>
